import { AccessControlListDc, BatchResourcesAclDc, Permissions, ResourceType } from "@evergis/api";
import { ChipAutoComplete, ChipAutoCompleteData, H3 } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUsername } from "../../../../ducks/user";
import { isReadable } from "../../../../evergis/core/permissions";
import { LayerInfo } from "../../../../interfaces/resourceInfo";
import { isConfigure, isWrite, togglePermissions } from "../../../../utils/permissions";
import { RestrictedWidthContainer } from "../../DialogComponents/styled";
import { AccessControl } from "../components/AccessControl";
import { CopyPublicResourceLink } from "../components/CopyPublicResourceLink";
import { getAclRole, getNameByRole } from "../getAclRole";
import { ProjectAccessDescription, ProjectControlsPanel } from "../ProjectAccess/styled";
import { CommonSettingsControls, CommonSettingsTitleContainer } from "../styled";
import { AccessRoleGroup } from "../types";
import { useUsersAndRoles } from "../useUsersAndRoles";

export type ProxyLayerAccessProps = {
  setUpdateData?: (updateData: BatchResourcesAclDc) => void;
  layerName: string;
  layer: LayerInfo;
};

// eslint-disable-next-line max-lines-per-function
export const ProxyLayerAccess: FC<ProxyLayerAccessProps> = ({ setUpdateData, layerName, layer }) => {
  const { t } = useTranslation("dialogs");

  const currentUser: string = useSelector(getUsername);

  const { users, usersAndRolesLoading, autoCompleteProps, pickedUsers, setPickedUsers } = useUsersAndRoles();
  const [layerAcl, setLayerAcl] = useState<AccessControlListDc>();
  const [activeUser, setActiveUser] = useState<string>("");

  const onPermissionAdd = useCallback(
    (userRole: string) => {
      setLayerAcl(currentLayerAcl => ({
        data: [...currentLayerAcl.data, { role: userRole, permissions: Permissions.Read }],
      }));
    },
    [setLayerAcl],
  );
  const onPermissionRemove = useCallback(
    (userRole: string) => {
      setLayerAcl(currentLayerAcl => ({
        data: currentLayerAcl.data.filter(({ role }) => role !== userRole),
      }));
    },
    [setLayerAcl],
  );
  const onPermissionChange = useCallback(
    (userRole: string, permission: "read" | "write" | "configure") => {
      setLayerAcl(currentLayerAcl => ({
        data: currentLayerAcl.data.map(({ role, permissions: userPermissions }) => {
          if (role !== userRole) {
            return { role, permissions: userPermissions };
          }

          return { role, permissions: togglePermissions(userPermissions, permission) };
        }),
      }));
    },
    [setLayerAcl],
  );

  const syncedPickedUsers = useRef(false);

  useEffect(() => {
    if (!syncedPickedUsers.current && users && !usersAndRolesLoading) {
      syncedPickedUsers.current = true;
      setLayerAcl(layer.acl);

      const aclRoles = layer.acl.data.filter(({ role }) => role !== `__${currentUser}`).map(({ role }) => role);

      setPickedUsers(aclRoles.map(role => getNameByRole(role, users)));
    }
  }, [layer.acl, users, currentUser]);

  useEffect(() => {
    setUpdateData({
      layers: [
        {
          objectName: layerName,
          data: layerAcl?.data || [],
        },
      ],
    });
  }, [layerAcl]);

  const getTextName = useCallback((name: string) => (name === AccessRoleGroup.Public ? t("access.public") : name), [t]);
  const readOnly = activeUser === AccessRoleGroup.Public;

  const { permissions = Permissions.None } =
    layerAcl?.data.find(({ role }) => role === getAclRole(activeUser, users)) || {};

  return (
    <>
      <RestrictedWidthContainer>
        <ProjectAccessDescription>{t("access.accessDescription")}</ProjectAccessDescription>
        <ChipAutoComplete
          loading={usersAndRolesLoading}
          activeItem={activeUser}
          items={users as ChipAutoCompleteData[]}
          activeItems={pickedUsers}
          onSelectItem={(name: string) => {
            setActiveUser(name);
            setPickedUsers(currentPickedUsers => [...currentPickedUsers, name]);
            onPermissionAdd(getAclRole(name, users));
          }}
          onRemoveItem={(name: string) => {
            setActiveUser("");
            setPickedUsers(currentPickedUsers => currentPickedUsers.filter(user => user !== name));
            onPermissionRemove(getAclRole(name, users));
          }}
          onActiveItem={(name: string) => {
            setActiveUser(name);
          }}
          slideButtonTitle={t("access.slide")}
          listButtonTitle={t("access.list")}
          placeholder={t("access.chooseUserName")}
          searchPlaceholder={t("access.search")}
          getTextName={getTextName}
          autoCompleteProps={autoCompleteProps}
        />
        {activeUser && (
          <>
            <CommonSettingsTitleContainer>
              <H3>{t("access.accessTitle")}</H3>
              <CommonSettingsControls>
                <AccessControl
                  type="read"
                  active={isReadable(permissions)}
                  clickable={!isReadable(permissions)}
                  onClick={() => !isReadable(permissions) && onPermissionChange(getAclRole(activeUser, users), "read")}
                />
                {!readOnly && (
                  <>
                    <AccessControl
                      type="write"
                      active={isWrite(permissions)}
                      onClick={() => onPermissionChange(getAclRole(activeUser, users), "write")}
                    />
                    <AccessControl
                      type="configure"
                      active={isConfigure(permissions)}
                      onClick={() => onPermissionChange(getAclRole(activeUser, users), "configure")}
                    />
                  </>
                )}
              </CommonSettingsControls>
            </CommonSettingsTitleContainer>
            <ProjectControlsPanel>
              {layerName && (
                <CopyPublicResourceLink
                  resourceType={ResourceType.Layer}
                  isPublicRole={activeUser === AccessRoleGroup.Public}
                  name={layerName}
                />
              )}
            </ProjectControlsPanel>
          </>
        )}
      </RestrictedWidthContainer>
    </>
  );
};
